import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/homeData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20 px-4 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium">Back to Home</Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="relative pt-24 pb-10 md:pt-32 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-accent-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-400">{article.content.breadcrumb}</span>
            </nav>

            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-xs font-semibold text-accent-600 mb-4">
              {article.content.tag}
            </span>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {article.content.h1}
            </h1>

            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.content.subtitle}
            </p>

            <div className="mt-6 flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                ACB Research Team
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>Updated: June 25, 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Featured Image */}
            <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-background-200 mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Dynamic Content Sections */}
            {article.content.sections.map((section, idx) => {
              if (section.type === 'heading') {
                return (
                  <section key={idx} className="mb-10">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                      {section.text}
                    </h2>
                  </section>
                );
              }

              if (section.type === 'paragraphs' && section.items) {
                return (
                  <section key={idx} className="mb-10">
                    {section.items.map((p, pi) => (
                      <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }}></p>
                    ))}
                  </section>
                );
              }

              if (section.type === 'subsections' && section.items) {
                return (
                  <section key={idx} className="mb-10">
                    {section.items.map((sub, si) => (
                      <div key={si} className="mb-5">
                        <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                          {sub.heading}
                        </h3>
                        <p className="text-sm md:text-base text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: sub.content }}></p>
                      </div>
                    ))}
                  </section>
                );
              }

              if (section.type === 'tips' && section.items) {
                return (
                  <section key={idx} className="mb-10">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                      {section.heading}
                    </h2>
                    <div className="space-y-5">
                      {section.items.map((tip, ti) => (
                        <div key={ti} className="flex gap-4">
                          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center text-sm font-bold">
                            {ti + 1}
                          </span>
                          <div>
                            <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-1">{tip.title}</h3>
                            <p className="text-xs md:text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: tip.content }}></p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              if (section.type === 'list' && section.items) {
                return (
                  <section key={idx} className="mb-10">
                    <ul className="space-y-2 ml-5 list-disc">
                      {section.items.map((li, lii) => (
                        <li key={lii} className="text-sm md:text-base text-foreground-700 leading-relaxed">{li}</li>
                      ))}
                    </ul>
                  </section>
                );
              }

              return null;
            })}

            {/* Comparison Table */}
            {article.content.comparison && (
              <section className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {article.content.comparison.heading}
                </h2>
                <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead>
                      <tr className="border-b border-background-200/70">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Aspect</th>
                        <th className="text-center px-5 py-3 text-xs font-semibold text-accent-600 uppercase tracking-wider bg-accent-50/50">With Spreadsheet</th>
                        <th className="text-center px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Without Spreadsheet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {article.content.comparison.rows.map((row, ri) => (
                        <tr key={ri} className={ri < article.content.comparison.rows.length - 1 ? 'border-b border-background-200/50' : ''}>
                          <td className="px-5 py-3 text-sm text-foreground-800 font-medium">{row.aspect}</td>
                          <td className="px-5 py-3 text-center bg-accent-50/30">
                            {row.withSpreadsheet === 'check' ? (
                              <i className="ri-check-line text-accent-500 text-base font-bold"></i>
                            ) : row.withSpreadsheet === 'cross' ? (
                              <i className="ri-close-line text-foreground-300 text-base"></i>
                            ) : (
                              <span className="text-sm font-semibold text-accent-600">{row.withSpreadsheet}</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-center">
                            {row.withoutSpreadsheet === 'cross' ? (
                              <i className="ri-close-line text-foreground-300 text-base"></i>
                            ) : row.withoutSpreadsheet === 'check' ? (
                              <i className="ri-check-line text-accent-500 text-base font-bold"></i>
                            ) : (
                              <span className="text-sm text-foreground-500">{row.withoutSpreadsheet}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* FAQ */}
            {article.content.faq && article.content.faq.length > 0 && (
              <section className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.content.faq.map((item, fi) => (
                    <details key={fi} className="group bg-background-100 rounded-xl border border-background-200/70">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="text-sm md:text-base font-semibold text-foreground-950 pr-4">{item.q}</span>
                        <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:bg-accent-500 group-open:text-background-50 transition-colors">
                          <i className="ri-add-line text-xs group-open:hidden"></i>
                          <i className="ri-subtract-line text-xs hidden group-open:block"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <section className="mb-10 bg-accent-50 rounded-2xl p-6 md:p-8 text-center border border-accent-200">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Ready to Explore the Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-5 max-w-lg mx-auto">
                Access the complete <strong>AllChinaBuy Spreadsheet</strong> with all 14 categories, trusted sellers, and verified QC photos.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all cursor-pointer"
                >
                  AllChinaBuy Sheet
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 border border-background-300 text-foreground-700 text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-all cursor-pointer"
                >
                  Back to Home
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </section>

            {/* Author box */}
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center text-accent-600 font-bold text-sm">ACB</span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">ACB Research Team</p>
                  <p className="text-xs text-foreground-500">5+ years researching buying guides and spreadsheets</p>
                </div>
              </div>
              <p className="text-xs text-foreground-600 leading-relaxed">
                Our content is regularly reviewed and updated to ensure accuracy. Editorial Guidelines: Every guide is fact-checked against current seller data and community feedback. Last reviewed: June 25, 2026.
              </p>
            </div>
          </div>
        </article>

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://allchinabuyspreadsheet.team/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.title,
                  item: `https://allchinabuyspreadsheet.team/guide/${article.slug}`,
                },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              author: { '@type': 'Organization', name: 'ACB Research Team' },
              datePublished: '2026-06-01',
              dateModified: '2026-06-25',
              publisher: { '@type': 'Organization', name: 'AllChinaBuy Spreadsheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg' } },
              image: article.imageUrl,
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://allchinabuyspreadsheet.team/guide/${article.slug}`,
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}